<template>
    <section v-if="recommendation" class="proposal-panel" :class="{ 'proposal-panel--review': needsReview }" aria-labelledby="proposal-title">
        <div class="d-flex align-start flex-wrap">
            <v-icon :color="tone.color" class="mr-3 mt-1">{{ tone.icon }}</v-icon>
            <div class="flex-grow-1 min-width-0">
                <div class="d-flex align-center flex-wrap proposal-heading">
                    <span class="overline" :class="`${tone.color}--text`">{{ needsReview ? "Analisi automatica" : "Proposta automatica" }}</span>
                    <v-chip x-small outlined :color="tone.color">{{ confidenceLabel }}</v-chip>
                </div>
                <h2 id="proposal-title" class="subtitle-1 font-weight-bold mb-1">{{ title }}</h2>
                <p class="body-2 mb-2">{{ recommendation.reason }}</p>
                <div v-if="recommendation.issue_categories && recommendation.issue_categories.length" class="proposal-tags">
                    <v-chip v-for="category in recommendation.issue_categories" :key="category" x-small outlined>
                        {{ category }}
                    </v-chip>
                </div>
                <div v-if="suggestionSummary" class="body-2 mt-2">
                    {{ suggestionSummary }}
                </div>
            </div>
            <v-btn
                v-if="hasApplicableSuggestions"
                small
                outlined
                color="primary"
                class="mt-2 mt-sm-0"
                @click="$emit('apply-suggestions')"
            >
                <v-icon small left>mdi-auto-fix</v-icon>Applica quantità
            </v-btn>
        </div>
        <div class="proposal-next-step mt-3">
            <v-icon small :color="tone.color" class="mr-2">mdi-clipboard-check-outline</v-icon>
            <div>
                <strong>{{ needsReview ? "Decidi dopo la verifica" : "Prima di confermare" }}</strong>
                <span class="d-block">{{ nextStep }}</span>
            </div>
        </div>
        <div v-if="recommendation.action !== 'mark_handled'" class="caption proposal-note mt-2">
            Cliente, articolo e quantità vanno verificati. Se crei una NC, la causale Sage è 901 · Materiale non conforme accettato in deroga.
        </div>
        <div v-if="!needsReview" class="proposal-feedback mt-3">
            <span class="body-2">La proposta è corretta?</span>
            <v-btn x-small text color="success" @click="feedback(true)">
                <v-icon x-small left>mdi-thumb-up-outline</v-icon>Sì
            </v-btn>
            <v-btn x-small text color="warning" @click="feedback(false)">
                <v-icon x-small left>mdi-pencil-outline</v-icon>No, da correggere
            </v-btn>
        </div>
    </section>
</template>

<script>
export default {
    name: "MailSegnalazioneProposal",
    props: {
        recommendation: { type: Object, default: null },
        articles: { type: Array, default: () => [] },
    },
    computed: {
        needsReview() {
            return this.recommendation?.action === "review";
        },
        title() {
            if (this.needsReview && this.recommendation?.mail_kind === "new_complaint") return "Possibile segnalazione: dati da completare";
            if (this.needsReview) return "Contenuto da verificare manualmente";
            return this.recommendation?.label || "Proposta da verificare";
        },
        nextStep() {
            if (this.needsReview) return "Leggi mail e allegati. Se è una segnalazione, conferma cliente e articoli prima di preparare la NC; altrimenti gestiscila senza NC.";
            if (this.recommendation?.action === "mark_handled") return "Controlla che la risposta non contenga nuovi problemi prima di segnare la mail come gestita.";
            return "Confronta il problema con la mail e gli allegati, poi conferma cliente, articoli e quantità prima di creare la NC.";
        },
        confidenceLabel() {
            const labels = { alta: "Confidenza alta", media: "Confidenza media", bassa: "Nessuna decisione" };
            return labels[this.recommendation?.confidence] || "da verificare";
        },
        tone() {
            if (this.recommendation?.action === "create_nc") return { color: "success", icon: "mdi-shield-search-outline" };
            if (this.recommendation?.action === "mark_handled") return { color: "grey", icon: "mdi-email-check-outline" };
            return { color: "warning", icon: "mdi-account-search-outline" };
        },
        hasApplicableSuggestions() {
            return this.articles.some((article) => article.suggestedQuantity);
        },
        suggestionSummary() {
            const quantity = this.recommendation?.suggested_quantity;
            const parts = [];
            if (quantity) parts.push(`Quantità rilevata: ${quantity} PZ`);
            return parts.join(" · ");
        },
    },
    methods: {
        feedback(accepted) {
            this.$emit("feedback", {
                kind: "recommendation",
                predicted_value: this.recommendation?.action || "",
                corrected_value: accepted ? this.recommendation?.action || "" : "needs_review",
                accepted,
                context: {
                    label: this.recommendation?.label || "",
                    mail_kind: this.recommendation?.mail_kind || "",
                    confidence: this.recommendation?.confidence || "",
                },
            });
        },
    },
};
</script>

<style scoped>
.proposal-panel {
    margin: 20px 0 4px;
    padding: 20px;
    border: 1px solid rgba(25, 118, 210, 0.28);
    border-left: 4px solid #1976d2;
    border-radius: 4px;
    background: rgba(25, 118, 210, 0.04);
}
.proposal-panel--review {
    border-color: rgba(251, 140, 0, 0.35);
    border-left-color: #fb8c00;
    background: rgba(251, 140, 0, 0.07);
}
.proposal-heading, .proposal-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.proposal-heading { margin-bottom: 4px; }
.proposal-next-step { display: flex; align-items: flex-start; font-size: 0.875rem; line-height: 1.5; }
.proposal-next-step .v-icon { flex: none; margin-top: 2px; }
.proposal-note { opacity: 0.8; line-height: 1.5; }
.proposal-feedback { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.min-width-0 { min-width: 0; }
</style>
